import React, { useEffect, useState } from 'react';
import { healthAPI } from '../api';
import { DailySummary as DailySummaryType } from '../types';
import { format, subDays, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import ReactMarkdown from 'react-markdown';
import { useUser } from '../contexts/UserContext';
import './DailySummary.css';

interface DailySummaryProps {
  refreshTrigger?: number;
  selectedDate?: string;
  onDateChange?: (date: string) => void;
  showAiInsights?: boolean;
  compact?: boolean;
  disableDateNavigation?: boolean;
}

const TIMEZONE = 'America/New_York';

const getTodayInEastern = (): string => {
  const now = new Date();
  const easternTime = toZonedTime(now, TIMEZONE);
  return format(easternTime, 'yyyy-MM-dd');
};

export const DailySummary: React.FC<DailySummaryProps> = ({
  refreshTrigger,
  selectedDate: externalSelectedDate,
  onDateChange,
  showAiInsights = true,
  compact = false,
  disableDateNavigation = false
}) => {
  const { profile } = useUser();
  const [summaries, setSummaries] = useState<DailySummaryType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [internalSelectedDate, setInternalSelectedDate] = useState<string>(getTodayInEastern());
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editSubContext, setEditSubContext] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);

  const selectedDate = externalSelectedDate || internalSelectedDate;

  // Get user's preferred units
  const waterUnit = profile?.settings?.units?.water || 'ml';
  const weightUnit = profile?.settings?.units?.weight || 'lbs';

  const loadDailySummary = async (date: string, force: boolean = false) => {
    setLoading(true);
    setError('');

    try {
      const summary = await healthAPI.getDailySummary(date, force);
      setSummaries([summary]);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load summary';
      console.error('Error loading daily summary:', err);

      if (errorMessage.includes('404') || errorMessage.includes('No events')) {
        // Don't show error for no events - this is expected for days without logs
        setSummaries([]);
      } else if (errorMessage.includes('401') || errorMessage.includes('Unauthorized')) {
        setError('Please sign in to view summaries');
        setSummaries([]);
      } else {
        setError(errorMessage);
        setSummaries([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDailySummary(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, refreshTrigger]);

  const handleDateChange = (days: number) => {
    if (disableDateNavigation) return;
    const newDate = format(subDays(parseISO(selectedDate), -days), 'yyyy-MM-dd');
    if (onDateChange) {
      onDateChange(newDate);
    } else {
      setInternalSelectedDate(newDate);
    }
  };

  const handleDateSelect = (date: string) => {
    if (disableDateNavigation) return;
    if (onDateChange) {
      onDateChange(date);
    } else {
      setInternalSelectedDate(date);
    }
  };

  const handleEditClick = () => {
    setEditSubContext(summaries[0]?.sub_context || '');
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditSubContext('');
  };

  const handleRegenerate = () => {
    if (!summaries[0]) return;
    loadDailySummary(summaries[0].date, true);
  };

  const handleSaveEdit = async () => {
    if (!summaries[0]) return;

    setSaving(true);
    try {
      await healthAPI.updateDailySummary(summaries[0].date, editSubContext);

      // Reload the summary to get the updated AI analysis
      setTimeout(() => {
        loadDailySummary(selectedDate);
        setIsEditing(false);
      }, 2000); // Wait 2 seconds for AI re-analysis
    } catch (err) {
      console.error('Error updating daily summary:', err);
      setError(err instanceof Error ? err.message : 'Failed to update summary');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={`daily-summary-container ${compact ? 'compact' : ''}`}>
        <div className="loading">Loading summary...</div>
      </div>
    );
  }

  return (
    <div className={`daily-summary-container ${compact ? 'compact' : ''}`}>
      {!compact && (
        <div className="summary-header">
          <h2>📝 Daily Summary</h2>
          <div className="date-navigation">
            <button onClick={() => handleDateChange(-1)} title="Previous day">
              ←
            </button>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => handleDateSelect(e.target.value)}
              max={getTodayInEastern()}
            />
            <button
              onClick={() => handleDateChange(1)}
              disabled={selectedDate === new Date().toISOString().split('T')[0]}
              title="Next day"
            >
              →
            </button>
          </div>
        </div>
      )}

      {compact && !disableDateNavigation && (
        <div className="summary-header-compact">
          <div className="date-navigation compact">
            <button onClick={() => handleDateChange(-1)}>←</button>
            <span className="current-date-label">{format(parseISO(selectedDate), 'MMM d, yyyy')}</span>
            <button
              onClick={() => handleDateChange(1)}
              disabled={selectedDate === new Date().toISOString().split('T')[0]}
            >→</button>
          </div>
        </div>
      )}

      {error ? (
        <div className="summary-error">{error}</div>
      ) : summaries.length > 0 ? (
        summaries.map((summary) => (
          <div key={summary.date} className="summary-card">
            {!compact && (
              <div className="summary-date">
                {format(parseISO(summary.date), 'EEEE, MMMM d, yyyy')}
                {summary.is_draft && <span className="draft-badge">Draft</span>}
              </div>
            )}

            <div className={`summary-stats ${compact ? 'compact-stats' : ''}`}>
              <div className="stat-row">
                <div className="stat">
                  <span className="stat-label">🚽 Bathroom</span>
                  <span className="stat-value">{summary.bathroom_count}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">🍽️ Meals</span>
                  <span className="stat-value">{summary.meal_count}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Total Events</span>
                  <span className="stat-value">{summary.event_count}</span>
                </div>
              </div>

              {/* Nutrition Summary (Horizontal Layout) */}
              <div className="nutrition-summary-horizontal">
                {!compact && <div className="nutrition-header">📊 Daily Nutrition Totals</div>}
                <div className="nutrition-badges-row">
                  <div className="summary-nutrition-badge calories">
                    <span className="badge-label">Cal</span>
                    <span className="badge-value">{summary.total_calories || 0}</span>
                  </div>
                  <div className="summary-nutrition-badge protein">
                    <span className="badge-label">P</span>
                    <span className="badge-value">{summary.total_protein || 0}g</span>
                  </div>
                  <div className="summary-nutrition-badge carbs">
                    <span className="badge-label">C</span>
                    <span className="badge-value">{summary.total_carbs || 0}g</span>
                  </div>
                  <div className="summary-nutrition-badge fat">
                    <span className="badge-label">F</span>
                    <span className="badge-value">{summary.total_fat || 0}g</span>
                  </div>
                  <div className="summary-nutrition-badge fiber">
                    <span className="badge-label">Fiber</span>
                    <span className="badge-value">{summary.total_fiber || 0}g</span>
                  </div>
                  <div className="summary-nutrition-badge sugar">
                    <span className="badge-label">Sugar</span>
                    <span className="badge-value">{summary.total_sugar || 0}g</span>
                  </div>
                  <div className="summary-nutrition-badge sodium">
                    <span className="badge-label">Na</span>
                    <span className="badge-value">{summary.total_sodium || 0}mg</span>
                  </div>
                </div>
              </div>

              {/* Biometrics Row */}
              <div className="biometrics-summary-horizontal">
                <div className="biometric-badge water">
                  <span className="biometric-label">💧 Water:</span>
                  <span className="biometric-value">
                    {waterUnit === 'oz'
                      ? `${Math.round((summary.total_water_ml || 0) / 29.5735)} oz`
                      : `${summary.total_water_ml || 0} ml`}
                  </span>
                </div>
                {summary.latest_weight && (
                  <div className="biometric-badge weight">
                    <span className="biometric-label">⚖️ Weight:</span>
                    <span className="biometric-value">
                      {(() => {
                        const weight = summary.latest_weight;
                        if (weightUnit === 'lbs') {
                          const lbs = weight.weight_lbs || (weight.weight_kg ? weight.weight_kg * 2.20462 : 0);
                          return `${lbs.toFixed(1)} lbs`;
                        } else {
                          const kg = weight.weight_kg || (weight.weight_lbs ? weight.weight_lbs / 2.20462 : 0);
                          return `${kg.toFixed(1)} kg`;
                        }
                      })()}
                    </span>
                  </div>
                )}
                {summary.latest_bp && (
                  <div className="biometric-badge bp">
                    <span className="biometric-label">🩺 BP:</span>
                    <span className="biometric-value">
                      {summary.latest_bp.systolic}/{summary.latest_bp.diastolic}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {showAiInsights && (
              <div className="ai-summary">
                <div className="ai-summary-header">
                  <span className="ai-icon">🤖</span>
                  <span className="ai-label">AI Summary</span>
                  {!isEditing && (
                    <div className="ai-actions">
                      <button
                        className="action-button"
                        onClick={handleRegenerate}
                        title="Regenerate summary"
                      >
                        🔄
                      </button>
                      <button
                        className="action-button"
                        onClick={handleEditClick}
                        title="Add context to regenerate summary"
                      >
                        ✏️
                      </button>
                    </div>
                  )}
                </div>

                {isEditing ? (
                  <div className="edit-form">
                    <label htmlFor="sub-context-input">
                      Add context to improve AI analysis (optional):
                    </label>
                    <textarea
                      id="sub-context-input"
                      value={editSubContext}
                      onChange={(e) => setEditSubContext(e.target.value)}
                      placeholder="E.g., 'Had unusual stress today', 'Traveled', 'Tried new medication', etc."
                      rows={3}
                      disabled={saving}
                    />
                    <div className="edit-actions">
                      <button
                        onClick={handleSaveEdit}
                        disabled={saving}
                        className="save-button"
                      >
                        {saving ? 'Saving & Re-analyzing...' : 'Save & Re-analyze'}
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        disabled={saving}
                        className="cancel-button"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {summary.sub_context && (
                      <div className="user-context">
                        <strong>Your context:</strong> {summary.sub_context}
                      </div>
                    )}
                    <div className="ai-summary-text">
                      <ReactMarkdown>{summary.summary}</ReactMarkdown>
                    </div>
                  </>
                )}
              </div>
            )}

            {
              showAiInsights && summary.key_observations && summary.key_observations.length > 0 && (
                <div className="key-observations">
                  <h4>Key Observations</h4>
                  <ul>
                    {summary.key_observations.map((obs, idx) => (
                      <li key={idx}>{obs}</li>
                    ))}
                  </ul>
                </div>
              )
            }
          </div >
        ))
      ) : (
        <div className="no-summary">
          No events logged for {format(parseISO(selectedDate), 'MMMM d, yyyy')}
        </div>
      )}
    </div >
  );
};
